import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/common";
import { OwlLoader } from "@/components/OwlLoader";
import { toast } from "sonner";
import api from "@/lib/api";
import { Send } from "lucide-react";

/** Who stalled mid-application, and one button to nudge them back. */
export const OnboardingNudges = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/nudge/pending");
      setRows(data.applicants || []);
    } catch (e) { setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/nudge/run?force=true");
      toast.success(`${data.sent} reminder${data.sent === 1 ? "" : "s"} sent`,
        { description: "Each one links back to the exact step they left." });
      load();
    } catch (e) {
      toast.error("Could not send the reminders");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 shadow-premium" data-testid="admin-onboarding-nudges">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-sm font-medium">Half-finished applications</div>
          <div className="text-xs text-muted-foreground">
            A reminder goes out automatically after 24 hours of silence, twice at most.
          </div>
        </div>
        <Button size="sm" className="rounded-full" onClick={send} disabled={busy || !rows?.length}
          data-testid="admin-nudge-send-btn">
          {busy ? <OwlLoader size="xs" /> : <Send className="h-3.5 w-3.5 me-1.5" />} Nudge now
        </Button>
      </div>

      {rows === null ? <OwlLoader size="sm" label="Looking…" /> : rows.length === 0 ? (
        <EmptyState compact testid="admin-nudges-empty" title="Nobody is stuck"
          hint="Every application in flight has been finished." />
      ) : (
        <Table data-testid="admin-nudges-table">
          <TableHeader><TableRow>
            <TableHead>Applicant</TableHead><TableHead>Stopped at</TableHead>
            <TableHead className="text-end">Step</TableHead><TableHead className="text-end">Reminders</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.email} data-testid={`admin-nudge-row-${r.email}`}>
                <TableCell>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.email}</div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.step_label}</TableCell>
                <TableCell className="text-end numeric text-sm">{r.step + 1}/13</TableCell>
                <TableCell className="text-end">
                  <Badge variant={r.nudges_sent ? "secondary" : "outline"}>{r.nudges_sent}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
};
